// Turning an attempt's `outputs` (the worker's whole report: artifact refs
// plus every measurement scalar the plugin set -- gen_s, peak_alloc_gb,
// phase times, VRAM breakdown, precision, the engine's notes) into rows for
// Mission Control's task detail. Admin-only by construction: the public
// Task view never carries these (02-core.md #9).

export interface MetaRow {
  key: string;
  /** Flat values render inline; nested objects/arrays render as JSON. */
  value: string;
  nested: boolean;
}

/** Artifact refs (`{artifact, sha256}`) are the result, not meta; they are
 * shown elsewhere. Everything else is meta, flat scalars first (sorted by
 * key), nested structures after. */
export function attemptMetaRows(outputs: Record<string, unknown> | undefined | null): MetaRow[] {
  if (!outputs) return [];
  const flat: MetaRow[] = [];
  const nested: MetaRow[] = [];
  for (const [key, raw] of Object.entries(outputs)) {
    if (isArtifactRef(raw)) continue;
    if (raw === null || raw === undefined) continue;
    if (typeof raw === "object") {
      nested.push({ key, value: JSON.stringify(raw, null, 2), nested: true });
    } else {
      flat.push({ key, value: formatScalar(raw), nested: false });
    }
  }
  const byKey = (a: MetaRow, b: MetaRow) => a.key.localeCompare(b.key);
  return [...flat.sort(byKey), ...nested.sort(byKey)];
}

function isArtifactRef(v: unknown): boolean {
  return typeof v === "object" && v !== null && !Array.isArray(v) && "artifact" in (v as Record<string, unknown>);
}

function formatScalar(v: unknown): string {
  if (typeof v === "number") return Number.isInteger(v) ? String(v) : String(Math.round(v * 1000) / 1000);
  return String(v);
}
